import { useCallback, useEffect, useMemo, useState } from "react";
import { getPocoPairsCount } from "../../challenges/utils/pocoSchedule";
import { sortRegistrationCategories } from "../../challenges/utils/registrationCategoryOrder";
import type {
  TournamentCategoryEditorField,
  TournamentCategoryEditorItem,
} from "../components/TournamentCategoriesEditor";
import {
  DEFAULT_TOURNAMENT_SHIRT_SIZES,
  buildUniqueTournamentCategoryKey,
  normalizeTournamentShirtSizes,
} from "../utils/tournamentCategories";

export type CreateTournamentCategoryModalState = {
  mode: "create" | "edit";
  originalCategoryKey: string | null;
  draft: TournamentCategoryEditorItem;
};

type UseAdminCreateTournamentCategoriesInput = {
  defaultCategoryPrefix: string;
  pocoCourtsCount?: number;
  variant?: "tournament" | "challenge" | "poco";
};

function getChallengeSlotLimit(registrationType: "individual" | "fixed_pair") {
  return registrationType === "fixed_pair" ? "6" : "12";
}

function buildDefaultCategory(
  defaultCategoryPrefix: string,
  index: number,
  slotLimit = "12",
  registrationType: "individual" | "fixed_pair" = "fixed_pair",
): TournamentCategoryEditorItem {
  return {
    categoryKey: `categoria-${index}`,
    categoryName: `${defaultCategoryPrefix} ${index}`,
    categoryMale: "6a",
    categoryFemale: "6a",
    categoryGender: "mixed",
    registrationType,
    address: "",
    scheduledAt: "",
    scheduledEndAt: "",
    shirtSizesEnabled: false,
    shirtSizes: [],
    slotLimit,
  };
}

export function useAdminCreateTournamentCategories({
  defaultCategoryPrefix,
  pocoCourtsCount = 3,
  variant = "tournament",
}: UseAdminCreateTournamentCategoriesInput) {
  const isChallengeVariant = variant === "challenge" || variant === "poco";
  const isPocoVariant = variant === "poco";
  const pocoPairLimit = getPocoPairsCount(pocoCourtsCount);
  const [tournamentCategories, setTournamentCategories] = useState<
    TournamentCategoryEditorItem[]
  >([]);
  const [activeTournamentCategoryKey, setActiveTournamentCategoryKey] =
    useState("");
  const [tournamentCategoryModal, setTournamentCategoryModal] =
    useState<CreateTournamentCategoryModalState | null>(null);

  useEffect(() => {
    if (!isPocoVariant) return;
    setTournamentCategories((current) =>
      current.map((category) => ({
        ...category,
        registrationType: "fixed_pair",
        courtsCount: category.courtsCount ?? pocoCourtsCount,
        slotLimit: String(getPocoPairsCount(category.courtsCount ?? pocoCourtsCount)),
      })),
    );
    setTournamentCategoryModal((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              registrationType: "fixed_pair",
              courtsCount: current.draft.courtsCount ?? pocoCourtsCount,
              slotLimit: String(getPocoPairsCount(current.draft.courtsCount ?? pocoCourtsCount)),
            },
          }
        : current,
    );
  }, [isPocoVariant, pocoCourtsCount]);

  const tournamentTotalSlots = useMemo(
    () =>
      tournamentCategories.reduce((acc, item) => {
        const value = Math.floor(Number(item.slotLimit || 0));
        if (!Number.isFinite(value) || value < 1) return acc;
        return acc + value;
      }, 0),
    [tournamentCategories],
  );

  const addTournamentCategory = useCallback(() => {
    const nextIndex = tournamentCategories.length + 1;
    const nextKey = buildUniqueTournamentCategoryKey(tournamentCategories);
    const registrationType = isPocoVariant
      ? "fixed_pair"
      : isChallengeVariant
        ? "individual"
        : "fixed_pair";
    setTournamentCategoryModal({
      mode: "create",
      originalCategoryKey: null,
      draft: {
        ...buildDefaultCategory(
          defaultCategoryPrefix,
          nextIndex,
          isPocoVariant
            ? String(pocoPairLimit)
            : isChallengeVariant
              ? getChallengeSlotLimit(registrationType)
              : "8",
          registrationType,
        ),
        categoryKey: nextKey,
        courtsCount: isPocoVariant ? pocoPairLimit / 2 : null,
      },
    });
  }, [
    defaultCategoryPrefix,
    isChallengeVariant,
    isPocoVariant,
    pocoPairLimit,
    tournamentCategories,
  ]);

  const editTournamentCategory = useCallback(
    (categoryKey: string) => {
      const category =
        tournamentCategories.find((item) => item.categoryKey === categoryKey) ||
        null;

      if (!category) {
        console.warn("Categoria não encontrada para edição:", categoryKey);
        return;
      }

      setTournamentCategoryModal({
        mode: "edit",
        originalCategoryKey: category.categoryKey,
        draft: { ...category },
      });
    },
    [tournamentCategories],
  );

  const updateTournamentCategoryModalDraft = useCallback(
    (field: TournamentCategoryEditorField, value: string) => {
      setTournamentCategoryModal((current) =>
        current
          ? {
              ...current,
              draft: {
                ...current.draft,
                [field]:
                  field === "slotLimit"
                    ? String(Math.max(1, Math.floor(Number(value || 1))))
                    : field === "registrationType"
                      ? isPocoVariant || value === "fixed_pair"
                        ? "fixed_pair"
                        : "individual"
                    : field === "shirtSizesEnabled"
                      ? value === "true"
                    : field === "shirtSizes"
                      ? normalizeTournamentShirtSizes(value)
                    : value,
                ...(isPocoVariant && field === "courtsCount"
                  ? { courtsCount: Number(value) === 4 ? 4 : 3, slotLimit: String(getPocoPairsCount(value)) }
                  : {}),
                ...(isChallengeVariant && field === "registrationType"
                  ? {
                      slotLimit: isPocoVariant
                        ? String(getPocoPairsCount(current.draft.courtsCount ?? pocoPairLimit / 2))
                        : getChallengeSlotLimit(
                            value === "fixed_pair"
                              ? "fixed_pair"
                              : "individual",
                          ),
                    }
                  : {}),
                ...(field === "shirtSizesEnabled" && value !== "true"
                  ? { shirtSizes: [] }
                  : {}),
                ...(field === "shirtSizesEnabled" &&
                value === "true" &&
                normalizeTournamentShirtSizes(current.draft.shirtSizes)
                  .length === 0
                  ? { shirtSizes: DEFAULT_TOURNAMENT_SHIRT_SIZES }
                  : {}),
              },
            }
          : current,
      );
    },
    [isChallengeVariant, isPocoVariant, pocoPairLimit],
  );

  const saveTournamentCategoryModal = useCallback(() => {
    if (!tournamentCategoryModal) return;
    const registrationType =
      !isPocoVariant &&
      tournamentCategoryModal.draft.registrationType === "individual"
        ? "individual"
        : "fixed_pair";
    const draft: TournamentCategoryEditorItem = {
      ...tournamentCategoryModal.draft,
      registrationType,
      categoryName:
        String(tournamentCategoryModal.draft.categoryName || "").trim() ||
        defaultCategoryPrefix,
      address: String(tournamentCategoryModal.draft.address || "").trim(),
      scheduledAt:
        String(tournamentCategoryModal.draft.scheduledAt || "").trim() || null,
      scheduledEndAt:
        String(tournamentCategoryModal.draft.scheduledEndAt || "").trim() ||
        null,
      shirtSizesEnabled: Boolean(
        tournamentCategoryModal.draft.shirtSizesEnabled,
      ),
      shirtSizes: normalizeTournamentShirtSizes(
        tournamentCategoryModal.draft.shirtSizes,
      ),
      slotLimit: String(
        isPocoVariant
          ? getPocoPairsCount(tournamentCategoryModal.draft.courtsCount ?? pocoPairLimit / 2)
          : isChallengeVariant
          ? Number(getChallengeSlotLimit(registrationType))
          : Math.max(
              1,
              Math.floor(Number(tournamentCategoryModal.draft.slotLimit || 1)),
            ),
      ),
    };
    if (!draft.shirtSizesEnabled) {
      draft.shirtSizes = [];
    }
    setTournamentCategories((current) =>
      sortRegistrationCategories(
        tournamentCategoryModal.mode === "edit"
          ? current.map((item) =>
              item.categoryKey === tournamentCategoryModal.originalCategoryKey
                ? draft
                : item,
            )
          : [...current, draft],
      ),
    );
    setActiveTournamentCategoryKey(draft.categoryKey);
    setTournamentCategoryModal(null);
  }, [
    defaultCategoryPrefix,
    isChallengeVariant,
    isPocoVariant,
    pocoPairLimit,
    tournamentCategoryModal,
  ]);

  const removeTournamentCategory = useCallback(
    (categoryKey: string) => {
      setTournamentCategories((current) => {
        const next = current.filter((item) => item.categoryKey !== categoryKey);
        if (
          !next.some((item) => item.categoryKey === activeTournamentCategoryKey)
        ) {
          setActiveTournamentCategoryKey(String(next[0]?.categoryKey || ""));
        }
        return next;
      });
    },
    [activeTournamentCategoryKey],
  );

  return {
    activeTournamentCategoryKey,
    addTournamentCategory,
    editTournamentCategory,
    removeTournamentCategory,
    saveTournamentCategoryModal,
    setActiveTournamentCategoryKey,
    setTournamentCategoryModal,
    tournamentCategories,
    tournamentCategoryModal,
    tournamentTotalSlots,
    updateTournamentCategoryModalDraft,
  };
}
